import React, {Component, lazy, Suspense} from "react";
import {Route, Routes, useParams} from "react-router-dom";
import {Alert} from "react-bootstrap";
import {Cookies, withCookies} from "react-cookie";
import {instanceOf} from "prop-types";
import {withTranslation} from "react-i18next";

// https://react-bootstrap.netlify.app/docs/components/navbar/#color-schemes
import 'bootstrap/dist/css/bootstrap.min.css';
import "../loader.js";

import {retrieveCurrentLocale} from "../i18n";
import {hasConsent, setCookies} from "../services/CookiesUtils";
import {isSet} from "../services/commonUtil";
import ApiV0 from '../services/ApiV0';
import CUtils from '../services/CUtils';

import CHead2 from "./CHead2";
import CFooter from "./CFooter2";
import CNotFound from "../_common/CNotFound";
import CCookiesModal from "../_common/CCookiesModal";
import CHome from "../home/CHome";
import CList from '../listings/CList';
import CListing from '../listing/CListing';
import CListingId from '../listing/CListingId';
import CCart from '../cart/CCart';
import CProfile from '../auth/CProfile';
import CLogin from '../auth/CLogin';
import CPassword from '../auth/CPassword';
import './styles/CApp2.css'
import './styles/CCommon.css'
import './styles/CRowSection.css'
import {renderLoader} from "../_common/CCommonUi";
import EventEmitter from "../services/EventEmitter";
import CInfoMessage from "../_common/CInfoMessage";

const CSearchResult = lazy(() => import('../home/CSearchResult'));

const CAdmin = lazy(() => import('../admin/CAdmin'));
const CAdminEditChListing = lazy(() => import('../admin/listings/CAdminEditChListing'));
const CAdminEditEtsyListing = lazy(() => import('../admin/listings/CAdminEditEtsyListing'));

const CRegister = lazy(() => import('../auth/CRegister'));
const CRemindPassword = lazy(() => import('../auth/CRemindPassword'));
const CCommand = lazy(() => import('../command/CCommand'));

const CWho = lazy(() => import('../officials/CWho'));
const CCGV = lazy(() => import('../officials/CCGV'));
const CAbout = lazy(() => import('../officials/CAbout'));
const CCredits = lazy(() => import('../officials/CCredits'));
const CContact = lazy(() => import('../officials/CContact'));
const CData = lazy(() => import('../officials/CData'));
const CLivraison = lazy(() => import('../officials/CLivraison'));


const currentPath = window.location.pathname;

let initialLoading = false;

/**
 * first load : /api/v0/me and then /api/v0/sections
 */
export default withCookies(withTranslation('common')(class CApp2 extends Component {
    static propTypes = {
        cookies: instanceOf(Cookies).isRequired,
    };

    constructor(props) {
        super(props);
        this.appContainer = React.createRef();
        setCookies(props.cookies);
        //~ Application initialization preferences : lang and cookies
        const newLang = retrieveCurrentLocale();
        ApiV0.changeLang(newLang);
        //~
        const showConsent = (!hasConsent() && !ApiV0.isBot()) || "/cookies" === currentPath;
        this.state = {
            infoMessage: null,
            errorMessage: null,
            modalMessage: null,// usedBy: onJustRegistered,onJustRemind
            me: null,
            showInfo: false,
            isConnected: false,
            isAdmin: false,
            sectionLoaded: false,
            showConsent,
            q: null
        };
    }

    hasComponentRequirements() {
        const {me, allSections} = this.state;
        return me && allSections;
    }

    componentDidMount() {
        if (this.state.errorMessage != null) {
            return;
        }
        if (initialLoading === false) {
            initialLoading = true;
            this.doMe();
        }
        document.body.addEventListener(EventEmitter.CH_SET_ADMIN, this.handleAdminChange.bind(this));
    }

    componentWillUnmount() {
        document.body.removeEventListener(EventEmitter.CH_SET_ADMIN, this.handleAdminChange.bind(this));
    }

    // events pushed by ApiV0::setAdminMenu, ApiV0::setAdminAction
    handleAdminChange(event) {
        const action = event.detail.action;
        if ("reloadSections" === action) {
            this.doLoadSections(true);
        }
    }

    doMe() {
        const app = this;
        ApiV0.me()
            .then(meResult => {
                const {
                    profile, unpaidCommands,
                    homeMessage, homeMessageEn
                } = meResult;
                const isConnected = profile?.displayName;
                const isAdmin = (isConnected && profile?.isAdmin === true);
                // redirect to current unpaid command if any
                const hasUnpaid = (unpaidCommands?.length > 0);
                if (hasUnpaid && currentPath.indexOf('/command') !== 0) {
                    document.location.href = "/command";
                    return;
                }

                // continue to load sections
                app.setState({
                    me: meResult,
                    isConnected, isAdmin,
                    homeMessage, homeMessageEn
                }, () => app.doLoadSections());
            })
            .catch(err => {
                console.warn(err);
                app.setState({me: {}}, () => app.doLoadSections());
            });
    }


    doLoadSections(force = false) {
        if (this.state.sectionLoaded === true && !force) {
            return;
        }
        // DEBUG // console.log("Capp::doLoadSections")
        const app = this;
        const {t} = this.props;
        ApiV0.getSections()
            .then(response => {
                const {sections, menus, menusTree} = response;
                // HOME SECTIONS : Only active, with slug and image
                const activeSections = sections.filter(
                    s => s.active_listing_count > 0 &&
                        isSet(s.slug) && isSet(s.en_slug) &&
                        s.images?.length > 0 &&
                        s.images[0]?.url_fullxfull
                );
                // DEBUG // console.log("CApp:: >> sections", JSON.stringify(activeSections))
                app.setState({
                    allSections: sections, sections: activeSections,
                    menus, menusTree,
                    sectionLoaded: true
                });
            })
            .catch(apiError => {
                const errorMessage = CUtils.userErrorOf("menu load error", t, apiError);
                console.error(errorMessage);
                app.setState({sections: [], sectionLoaded: true});
            });
    }

    onJustRegistered() {
        const {t} = this.props;
        this.setState({modalMessage: t('app.registered'), modalMessageBis: t('app.registeredBis')});
        this.infoAutoHide();
    }

    onJustRemind() {
        const {t} = this.props;
        this.setState({modalMessage: t('app.reminded'), modalMessageBis: t('app.remindedBis')});
        this.infoAutoHide();
    }

    onJustCompleted() {
        // nothing here
    }

    onInvalidToken(message) {
        this.setState({modalMessage: message});
        this.infoAutoHide();
    }

    onCartUpdated(infoMessage) {
        if (infoMessage !== undefined && infoMessage !== null && infoMessage !== '') {
            this.setState({infoMessage});
            this.infoAutoHide();
        }
        this.doMe();
    }

    onProfileUpdated(infoMessage) {
        if (infoMessage) {
            this.setState({infoMessage});
            this.infoAutoHide();
        }
        this.doMe();
    }

    cartEntryQuantity(listing_id, variationKey, variationValue, customizationValue) {
        if (!this.state.me || !this.state.me.cart) {
            return 0;
        }
        const cart = this.state.me.cart;
        let entries;
        if (isSet(customizationValue)) {
            entries = cart.entries.filter(e => {
                return (e.listing_id === listing_id && e.customization_value === customizationValue);
            });
        } else if (isSet(variationKey) && isSet(variationValue)) {
            entries = cart.entries.filter(e => {
                return (e.listing_id === listing_id && e.variation_key === variationKey && e.variation_value === variationValue);
            });
        } else {
            entries = cart.entries.filter(e => {
                return (e.listing_id === listing_id);
            });
        }
        const reducerAdd = (accumulator, currentValue) => accumulator + currentValue;
        return Array.isArray(entries) ? entries.map(e => e.quantity).reduce(reducerAdd, 0) : 0;
    }

    cartFindEntry(listing_id, variationKey, variationValue, customizationValue) {
        if (!this.state.me || !this.state.me.cart) {
            return null;
        }
        const cart = this.state.me.cart;
        if (isSet(customizationValue)) {
            return cart.entries.find(e => {
                return (e.listing_id === listing_id && e.customization_value === customizationValue);
            });
        }
        if (isSet(variationKey) && isSet(variationValue)) {
            return cart.entries.find(e => {
                return (e.listing_id === listing_id && e.variation_key === variationKey && e.variation_value === variationValue);
            });
        }
        return cart.entries.find(e => {
            return (e.listing_id === listing_id);
        });
    }

    infoHide() {
        this.setState({showInfo: false});
    }

    infoAutoHide(timeoutMs = 50000) {
        const app = this;
        this.setState({
            showInfo: true
        }, () => {
            setTimeout(() => {
                app.infoHide();
            }, timeoutMs)
        });
    }


    renderCAppContent() {
        const {
            me,
            isAdmin,
            isConnected,
            allSections,
            sections,
            menus,
            "q": searchValue
        } = this.state;
        const isForm = ["/login", "/register", "/remind", "/contact", "/me"].includes(document.location.pathname)
        const customContentStyle = isForm ? "CFormContent" : "";
        const homeMessage = me?.homeMessage?.fr || "";
        const homeMessageEn = me?.homeMessage?.en || "";
        const cartMessage = me?.cartMessage?.fr || "";
        const cartMessageEn = me?.cartMessage?.en || "";
        const messages = {homeMessage, homeMessageEn, cartMessage, cartMessageEn};
        return (
            <div id="CContainer">
                <div id="CContentNormal">
                    <div id="CMainContent" className={customContentStyle}>
                        <Routes>
                            {/* Home */}
                            {["/", "/home", "/cookies"].map((path, index) => {
                                return (
                                    <Route path={path} key={index}
                                           element={<CHome me={me} sections={sections}/>}/>
                                );
                            })}

                            {/* Auth */}
                            <Route path="/login" element={<CLogin/>}/>
                            <Route path="/register" element={<Suspense fallback={renderLoader()}>
                                <CRegister onJustRegistered={this.onJustRegistered.bind(this)}/>
                            </Suspense>}/>
                            <Route path="/remind" element={<Suspense fallback={renderLoader()}>
                                <CRemindPassword onJustRemind={this.onJustRemind.bind(this)}/>
                            </Suspense>}/>
                            <Route path="/cb/:tokenId"
                                   element={<CallbackPassword onJustRegistered={this.onJustCompleted.bind(this)}
                                                              onInvalidToken={this.onInvalidToken.bind(this)}/>}/>

                            {/* Body */}
                            <Route path="/listing/:sectionId" element={<ShowListingSection/>}/>
                            <Route path="/listing/:sectionId/:listingId"
                                   element={<ShowListing onCartUpdated={this.onCartUpdated.bind(this)}
                                                         cartFindEntry={this.cartFindEntry.bind(this)}
                                                         cartEntryQuantity={this.cartEntryQuantity.bind(this)}
                                                         isAdmin={isAdmin}
                                                         isConnected={isConnected}/>}/>
                            <Route path="/lid/:listingId" element={<ShowListingId/>}/>
                            <Route path="/listing" element={<CList/>}/>

                            {/* Head */}
                            <Route path="/me" element={<CProfile me={me}
                                                                 updated={this.onProfileUpdated.bind(this)}/>}/>
                            <Route path="/cart" element={<CCart me={me}
                                                                onUpdated={this.onProfileUpdated.bind(this)}
                                                                onCartUpdated={this.onCartUpdated.bind(this)}/>}/>
                            <Route path="/command/*" element={<Suspense fallback={renderLoader()}>
                                <CCommand me={this.state.me}
                                          onUpdated={this.onProfileUpdated.bind(this)}
                                          onProfileUpdated={this.onProfileUpdated.bind(this)}/>
                            </Suspense>}/>
                            <Route path="/search" element={<Suspense fallback={renderLoader()}>
                                <CSearchResult searchValue={searchValue}/>
                            </Suspense>}/>
                            {/* Footer */}
                            <Route path="/about" element={<Suspense fallback={renderLoader()}><CAbout/></Suspense>}/>
                            <Route path="/cgv" element={<Suspense fallback={renderLoader()}><CCGV/></Suspense>}/>
                            <Route path="/livraison"
                                   element={<Suspense fallback={renderLoader()}><CLivraison/></Suspense>}/>
                            <Route path="/who" element={<Suspense fallback={renderLoader()}><CWho/></Suspense>}/>
                            <Route path="/contact"
                                   element={<Suspense fallback={renderLoader()}><CContact/></Suspense>}/>
                            <Route path="/donnees" element={<Suspense fallback={renderLoader()}><CData/></Suspense>}/>
                            <Route path="/credits"
                                   element={<Suspense fallback={renderLoader()}><CCredits/></Suspense>}/>

                            {/* Admin */}
                            {isAdmin ? (<Route path="/admin/listings"
                                               element={<EditChListing sections={allSections}/>}/>) : null}
                            {isAdmin ? (<Route path="/admin/listing/:sectionId/:listingId"
                                               element={<EditEtsyListing/>}/>) : null}
                            {isAdmin ? (<Route path="/admin" element={<Suspense fallback={renderLoader()}>
                                <CAdmin me={me}
                                        updated={this.onProfileUpdated.bind(this)}
                                        menus={menus} messages={messages}
                                />
                            </Suspense>}/>) : null}

                            {/* default: not found */}
                            <Route path='*' element={<CNotFound/>}/>
                        </Routes>
                    </div>
                </div>
            </div>
        );
    }


    render() {
        const {
            sections, menusTree, showConsent,
            errorMessage,
            showInfo, infoMessage,
            modalMessage, modalMessageBis
        } = this.state;
        const errorMessageTag = errorMessage ? <Alert variant="warning">{errorMessage}</Alert> : null;

        const hasComponentRequirements = this.hasComponentRequirements();
        if (!hasComponentRequirements && errorMessage) {
            return errorMessageTag;
        } else if (!hasComponentRequirements) {
            return renderLoader();
        }

        const infoMessageTag = <CInfoMessage msg={infoMessage} show={showInfo}/>;
        const messages = (<span>{errorMessageTag}{infoMessageTag}</span>);
        const modalMessageTag = modalMessage ? <div className="modalParent">
            <div className="modalChild">{modalMessage}<br/>{modalMessageBis}</div>
        </div> : null;
        const cContainer = this.renderCAppContent();

        return (
            <div className="chapp website-font-change" ref={this.appContainer}>
                {showConsent ? (<CCookiesModal/>) : null}
                <CHead2 me={this.state.me} sections={sections} menusTree={menusTree}/>
                {modalMessageTag ? modalMessageTag
                    : <>{messages}{cContainer}</>}
                <CFooter/>
            </div>
        );
    }
}));


function CallbackPassword(props) {
    const params = useParams();
    return (<CPassword tokenId={params.tokenId} onJustRegistered={props.onJustRegistered}
                       onInvalidToken={props.onInvalidToken}/>);
}

function ShowListingSection() {
    const params = useParams();
    return (<CList sectionId={params.sectionId}/>);
}

function ShowListing(props) {
    const params = useParams();
    return (<CListing onCartUpdated={props.onCartUpdated}
                      cartFindEntry={props.cartFindEntry}
                      cartEntryQuantity={props.cartEntryQuantity}
                      sectionId={params.sectionId}
                      listingId={params.listingId}
                      isAdmin={props.isAdmin}
                      isConnected={props.isConnected}
    />);
}

function ShowListingId() {
    const params = useParams();
    return (<CListingId listingId={params.listingId}/>);

}

function EditChListing(props) {
    return (<Suspense fallback={renderLoader()}><CAdminEditChListing sections={props.sections}/></Suspense>);
}

function EditEtsyListing() {
    const params = useParams();
    return (<Suspense fallback={renderLoader()}><CAdminEditEtsyListing listingId={params.listingId}
                                                                       sectionId={params.sectionId}/></Suspense>);
}
